import React from 'react';
import BlockRenderer from './blocks/BlockRenderer';

/**
 * ============================================================================
 * CmsBlocksRenderer - Rendu des blocs CMS avec Layout Container
 * ============================================================================
 * 
 * DESIGN SYSTEM ARCHITECTURE (REFACTOR V2 - MAI 2026):
 * - RÉFÉRENCE UNIQUE: max-w-7xl = 1280px
 * - Aligné sur HeaderBlock, MenuBlock, DashboardLayout
 * - Le LAYOUT contrôle la structure (largeur, centrage, padding)
 * - Les BLOCS sont passifs (width: 100% du parent)
 * 
 * ZONES:
 * - header: Bannières (layout standard 1280px)
 * - content: Contenu principal (layout standard 1280px)
 * - footer: Pied de page (layout standard 1280px)
 * 
 * RÈGLE: Tous les blocs sont enveloppés dans un layout container
 * qui définit la largeur maximale et le centrage.
 */

// Configuration des layouts par zone - UNIFIÉ 1200px (aligné sur PageBlocksRenderer / Accueil)
const ZONE_LAYOUT_CONFIG = {
  header: {
    maxWidth: '1200px',      // UNIFIÉ: aligné sur PageBlocksRenderer
    paddingX: '24px',        // px-6 — cohérent avec Accueil
    paddingY: '24px',
    gap: '24px'
  },
  content: {
    maxWidth: '1200px',      // UNIFIÉ: aligné sur PageBlocksRenderer
    paddingX: '24px',        // px-6 — cohérent avec Accueil
    paddingY: '16px',
    gap: '32px'
  },
  footer: {
    maxWidth: '1200px',      // UNIFIÉ: aligné sur PageBlocksRenderer
    paddingX: '24px',        // px-6 — cohérent avec Accueil
    paddingY: '24px',
    gap: '24px'
  },
  default: {
    maxWidth: '1200px',      // UNIFIÉ: aligné sur PageBlocksRenderer
    paddingX: '24px',        // px-6 — cohérent avec Accueil
    paddingY: '24px',
    gap: '24px'
  }
};

const CmsBlocksRenderer = ({ 
  blocks = [], 
  zone = 'content',
  pageType = 'default',
  className = ''
}) => {
  if (!blocks || blocks.length === 0) {
    return null;
  }

  // Configuration du layout pour cette zone
  const layoutConfig = ZONE_LAYOUT_CONFIG[zone] || ZONE_LAYOUT_CONFIG.default;

  return (
    <div 
      className={`cms-blocks-zone cms-zone-${zone} ${className}`}
      data-cms-zone={zone}
      data-page-type={pageType}
      style={{
        // ============================================================
        // LAYOUT CONTAINER - Source de vérité pour la largeur
        // Les blocs enfants héritent width: 100%
        // ============================================================
        maxWidth: layoutConfig.maxWidth,
        marginLeft: 'auto',
        marginRight: 'auto',
        paddingLeft: layoutConfig.paddingX,
        paddingRight: layoutConfig.paddingX,
        paddingTop: layoutConfig.paddingY,
        paddingBottom: layoutConfig.paddingY,
        width: '100%',
        boxSizing: 'border-box'
      }}
    >
      {/* Container flex pour le gap entre blocs */}
      <div 
        className="cms-blocks-container"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: layoutConfig.gap,
          width: '100%'
        }}
      >
        {blocks.map((block) => (
          <div 
            key={block.id} 
            className="cms-block-wrapper"
            data-block-id={block.id}
            data-block-type={block.type}
            style={{
              // Chaque bloc prend 100% de la largeur du container
              width: '100%'
            }}
          >
            <BlockRenderer block={block} />
          </div>
        ))}
      </div>
    </div>
  );
};

export default CmsBlocksRenderer;
